import React, { useState, useEffect, useRef } from 'react'
import Grid from '@material-ui/core/Grid'
import Divider from '@material-ui/core/Divider'
import Typography from '@material-ui/core/Typography'
import Button from '@material-ui/core/Button'
import Box from '@material-ui/core/Box'
import { ThemeProvider } from '@material-ui/core/styles'
import AccountsBenefitModal from 'components/account-benefits-modal'
import { useStyles } from './styles'
import { FormProvider, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers'
import { validationSchema } from '../utils'
import qs from 'query-string'
import SelectLessonPack from '../components/SelectLessonPack'
import { useLocation } from 'react-router-dom'
import VerifyAccessCode from 'components/verify-access-code'
import BackButton from 'components/back-button'
import themes, { extendTheme } from 'themes'
import CoachLessonHeader from '../components/CoachLessonHeader'
import { ContainerRoot, WaiverContainer } from '../components/styles'
import {
  COACHING_CENTER_SLUGS,
  COACHING_CENTER_HOLIDAY_LESSON_TYPE_IDS,
  FRISCO_COACHING_CENTER_FACILITY_ID,
} from 'utils/constants'
import { CoachingCenterWaiver } from 'components/waivers'
import TermsAndConditions from '../components/TermsAndConditions'
import { redirectToLogin, redirectToSignup } from 'lib/auth'
import MarketingOptIn from 'components/marketing-opt-in/marketing-opt-in'
import { useGetClientSecret } from './utils'
import StripePaymentElementsForm from './stripe-payment-elements-form'
import CancellationPolicy from 'components/cancellation-policy'
import { toBoolean } from 'lib/utils/boolean'
import { amplitudeEvent } from 'lib/amplitude'

const BuyLessonPackLayout = ({
  previousUrl,
  lessonInfo,
  user,
  setSelectedLessonPackId,
  selectedLessonPackId,
}) => {
  const [marketingOptInChecked, setMarketingOptInChecked] = useState(null)
  const { clientSecret, loading: secretLoading } = useGetClientSecret(
    selectedLessonPackId,
  )

  const [isOpen, setIsOpen] = useState(false)
  const classes = useStyles()
  const [isChecked, setIsChecked] = useState(false)
  const { coach, requiresAccessCode } = lessonInfo

  const ccAffiliatedLessonPack = previousUrl.includes(
    'coaching-center' || COACHING_CENTER_SLUGS,
  )
  const coachBooksOnBehalfOfStudent = user?.externalId === coach?.externalId
  const shouldShowWaiver =
    COACHING_CENTER_SLUGS.includes(lessonInfo?.academy?.slug) &&
    !coachBooksOnBehalfOfStudent
  const [showWaiver, setShowWaiver] = useState(false)
  const userIsNotOptedIn = !user?.marketingOptInAt

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [showWaiver])

  useEffect(() => {
    amplitudeEvent('view-checkout-screen', {
      payment_required: true,
      purchase_type: 'lesson_pack',
      payment_intent_enabled: true,
    })
  }, [])

  const form = useForm({
    resolver: yupResolver(validationSchema(true, shouldShowWaiver, true)),
    mode: 'onTouched',
    shouldFocusError: true,
  })
  const {
    register,
    formState: { isValid },
  } = form
  const location = useLocation()

  const { lessonTypeId, locationId } = qs.parse(location.search)

  const [verifiedAccessCode, setVerifiedAccessCode] = useState(
    !requiresAccessCode || location?.state?.verifiedAccessCode,
  )
  const [isSubmitting, setIsSubmitting] = useState(false)
  const stripePaymentFormRef = useRef(null)

  const isHolidayPack =
    COACHING_CENTER_HOLIDAY_LESSON_TYPE_IDS.includes(lessonTypeId) &&
    FRISCO_COACHING_CENTER_FACILITY_ID === locationId

  const showTermsAndConditions = isHolidayPack

  const canSubmit = showTermsAndConditions
    ? isValid && isChecked && !isSubmitting && selectedLessonPackId
    : isValid && !isSubmitting && selectedLessonPackId

  const handleButtonClick = async () => {
    if (shouldShowWaiver) {
      setShowWaiver(true)
      return
    }

    await form.handleSubmit(onSubmit)()
  }

  const onSubmit = async () => {
    setIsSubmitting(true)
    if (stripePaymentFormRef.current?.submitPayment) {
      return stripePaymentFormRef.current.submitPayment()
    }
  }

  return (
    <>
      <WaiverContainer showWaiver={showWaiver}>
        <CoachingCenterWaiver
          form={form}
          showWaiver={showWaiver}
          setShowWaiver={setShowWaiver}
          onSubmit={onSubmit}
          withBackButton={true}
        />
      </WaiverContainer>
      <ContainerRoot
        className={classes.rootRefresh}
        style={{ display: showWaiver ? 'none' : 'block' }}
      >
        <ThemeProvider theme={extendTheme(themes.offeringsRefresh)}>
          <BackButton previousUrl={previousUrl} />
          <CoachLessonHeader lessonType={lessonInfo} coach={coach} />
          <Grid item xs={12}>
            <Divider className={classes.divider} />
          </Grid>
          <Grid container spacing={2}>
            <Grid container item md={6} xs={12} className={classes.textWrapped}>
              <Box display={user ? 'grid' : 'none'}>
                <SelectLessonPack
                  lessonInfo={lessonInfo}
                  selectedLessonPackId={selectedLessonPackId}
                  setSelectedLessonPackId={setSelectedLessonPackId}
                />
                <FormProvider {...form}>
                  <form>
                    <Grid item md={9} sm={10} xs={12}>
                      <CancellationPolicy
                        policy={
                          toBoolean(coach?.canStudentCancel)
                            ? coach?.cancellationPolicy
                            : null
                        }
                      />
                      <Box mb={4}>
                        <StripePaymentElementsForm
                          ref={stripePaymentFormRef}
                          clientSecret={clientSecret}
                          secretLoading={secretLoading}
                          isSubmitting={isSubmitting}
                          setIsSubmitting={setIsSubmitting}
                          coachSlug={coach?.coachProfile?.slug}
                          isCoachingCenterPurchase={ccAffiliatedLessonPack}
                          marketingOptInChecked={marketingOptInChecked}
                        />
                      </Box>
                      {userIsNotOptedIn && (
                        <MarketingOptIn
                          checked={marketingOptInChecked}
                          onChange={setMarketingOptInChecked}
                          useMarketingOptIn={'callout'}
                        />
                      )}
                      {showTermsAndConditions && (
                        <TermsAndConditions
                          isChecked={isChecked}
                          setIsChecked={setIsChecked}
                          register={register}
                        />
                      )}
                    </Grid>
                    <Grid item md={5}>
                      <Box mb={4} textAlign="left">
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={handleButtonClick}
                          className={classes.buttonStyles}
                          data-testid="complete-booking-button"
                          disabled={!canSubmit}
                        >
                          {shouldShowWaiver ? 'Continue' : 'Complete Purchase'}
                        </Button>
                      </Box>
                    </Grid>
                  </form>
                </FormProvider>
              </Box>
              <Box display={user ? 'none' : 'grid'} mb={5}>
                <Typography variant="h5">
                  Sign in or create an account
                </Typography>
                <Typography variant="body" className={classes.warningBox}>
                  You must have a MyPGA account to book this session.{' '}
                  <span
                    onClick={() => setIsOpen(true)}
                    className={classes.learnMoreText}
                  >
                    Learn more
                  </span>
                </Typography>
                <Box display="flex">
                  <Grid container spacing={2}>
                    <Grid item xs={7} md={5}>
                      <Button
                        onClick={() => redirectToSignup('consumer')}
                        variant="contained"
                        color="primary"
                        size="large"
                        fullWidth={true}
                        style={{ textAlign: 'center' }}
                      >
                        Create an Account
                      </Button>
                    </Grid>
                    <Grid item xs={4} md={5}>
                      <Button
                        onClick={() => redirectToLogin('consumer')}
                        variant="outlined"
                        color="primary"
                        size="large"
                        fullWidth={true}
                        style={{ textAlign: 'center' }}
                      >
                        Sign In
                      </Button>
                    </Grid>
                  </Grid>
                </Box>
              </Box>
            </Grid>
          </Grid>
          <AccountsBenefitModal isOpen={isOpen} setIsOpen={setIsOpen} />
          <VerifyAccessCode
            lessonType={lessonInfo}
            isOpen={!verifiedAccessCode}
            setIsOpen={setVerifiedAccessCode}
            coachName={coach.name}
            setVerifiedAccessCode={setVerifiedAccessCode}
          />
        </ThemeProvider>
      </ContainerRoot>
    </>
  )
}

export default BuyLessonPackLayout
